import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn, Button } from '@tailoring-crm/ui';

/**
 * What a screen shows when the request behind it failed.
 *
 * Not the same thing as `EmptyState`, and the distinction is the whole point: an empty list says
 * "there is nothing here yet", a failed request says "we do not know what is here". A calendar
 * that renders a refused week as a free one gets double-booked; a stock list that renders a
 * refused fetch as empty gets reordered. Every screen that reads a query branches on its error
 * state through this, so the two never look alike.
 *
 * Strings arrive already translated; the defaults are the shared copy.
 */
export interface LoadErrorProps {
  /** Re-runs the query. Wired to `refetch`, never to a page reload. */
  onRetry?: () => void;
  title?: string;
  description?: string;
  className?: string;
}

export function LoadError({ onRetry, title, description, className }: LoadErrorProps) {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-6 py-10 text-center',
        className,
      )}
    >
      <span className="flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle aria-hidden="true" className="size-6" />
      </span>
      <div className="flex flex-col gap-1">
        <p className="text-base font-semibold">{title ?? t('error.load.title')}</p>
        <p className="mx-auto max-w-sm text-sm text-muted-foreground">
          {description ?? t('error.load.description')}
        </p>
      </div>
      {onRetry ? (
        <div className="pt-1">
          <Button variant="outline" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
